import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Checkbox,
  Stack,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import dayjs from "dayjs";
import { Box } from "@mui/system";
import { useTranslation } from "react-i18next";
import { SuppliesDetailObject } from "../../../../core/models/suppliesDetail/suppliesDetail.model";
import { useEffect, useState } from "react";
import ManagementsUpdateNewPowersTable from "./ManagementsUpdateNewPowersTable/ManagementsUpdateNewPowersTable";
import ButtonElement from "../../../UI/ButtonElement/ButtonElement";
import LoadingComponentComponent from "../../../UI/LoadingComponentComponent/LoadingComponentComponent";
import { NewPowers } from "../../../../core/models/managementsUpdate/managementsUpdatePowers/managementsUpdatePowers";
import { formatUpdatePowersRequestBody } from "../../../../utils/components/managementsUpdate/managementsUpdatePowers/managementsUpdatePowers.util";
import ManagementUploadFile from "../../../Managements/ManagementsUploadFile/ManagementsUploadFile";
import ManagementsCancelProcess from "../../../Managements/ManagementsCancelProcess/ManagementsCancelProcess";
import SingleDatePicker from "../../../UI/SingleDatePicker/SingleDatePicker";
import { useSingleDatePickerValidation } from "../../../../utils/components/datePickers/datePickers.utils";
import { CustomDateError } from "../../../../core/models/datePickersComponent/datePickersComponent.model";
import { formatDate } from "../../../../utils/dateUtils/formatDate.util";

interface ManagementsUpdateNewPowersProps {
  cupsDetail: SuppliesDetailObject;
  isLoading: boolean;
  handleSubmit: (incidenceText: string) => void;
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
  setRequiredFile: React.Dispatch<React.SetStateAction<File | null>>;
  isAgent: boolean;
  requiredFile: File | null;
  ocrError: "response_ko" | "server_error" | null;
  isOcrLoading: boolean;
  newPowers: NewPowers;
  setNewPowers: React.Dispatch<React.SetStateAction<NewPowers>>;
  maximeter: boolean;
  setMaximeter: React.Dispatch<React.SetStateAction<boolean>>;
  setAppliedDayFormatted: React.Dispatch<React.SetStateAction<string | null>>;
  ocrErrorMessage: string[];
}

export interface ContactData {
  phone: string;
  contactName: string;
}

const ManagementsUpdateNewPowers = ({
  cupsDetail: { powers, rate },
  isLoading,
  handleSubmit,
  setFile,
  setRequiredFile,
  isAgent,
  requiredFile,
  ocrError,
  isOcrLoading,
  newPowers,
  setNewPowers,
  maximeter,
  setMaximeter,
  setAppliedDayFormatted,
  ocrErrorMessage,
}: ManagementsUpdateNewPowersProps) => {
  const { t } = useTranslation();
  const [checkBoxRadioValue, setCheckBoxRadioValue] = useState<string>("");
  const [isConsentAccepted, setIsConsentAccepted] = useState<boolean>(false);
  const [appliedDay, setAppliedDay] = useState<dayjs.Dayjs | null>(null);
  const [notes, setNotes] = useState<string>("");
  const [contactData, setContactData] = useState<ContactData>({
    phone: "",
    contactName: "",
  });
  const [hasErrorPowers, setHasErrorPowers] = useState<boolean>(false);
  const [hasMinPowerError, setHasMinPowerError] = useState<boolean>(false);
  const isSamePowersLength =
    powers.length === Object.values(newPowers).length &&
    !Object.values(newPowers).some((newPower) => !newPower.length);
  const { error, setError } = useSingleDatePickerValidation();
  const isDisabled =
    !!!Object.values(newPowers)[0]?.length ||
    hasErrorPowers ||
    !isConsentAccepted ||
    !!!checkBoxRadioValue.length ||
    (checkBoxRadioValue === "selectDate" && !appliedDay) ||
    (!rate.startsWith("2") && !isSamePowersLength) ||
    (rate.startsWith("2") && !newPowers.p3?.length) ||
    hasMinPowerError ||
    (isAgent && !!!requiredFile) ||
    !!ocrError ||
    !!error;

  useEffect(() => {
    setHasErrorPowers(checkPowersError(newPowers));
  }, [newPowers]);

  const appliedDayData = [
    {
      id: "asap",
      label: "Cuanto antes",
      value: "asap",
    },
    {
      id: "selectDate",
      label: "Seleccionar fecha",
      value: "selectDate",
    },
  ];

  const checkPowersError = (newPowers: NewPowers) =>
    Object.values(newPowers).some(
      (newPower, indexNumber, newPowersArray) =>
        !!indexNumber &&
        newPower.length &&
        +newPowersArray[indexNumber - 1] > +newPower,
    );

  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const getPowerMessageKey = (rate: string) => {
    if (rate.includes("6")) {
      if (rate.includes("1")) {
        return "Tarifa6.1TD";
      } else if (rate.includes("2")) {
        return "Tarifa6.2TD";
      } else if (rate.includes("3")) {
        return "Tarifa6.3TD";
      } else if (rate.includes("4")) {
        return "Tarifa6.4TD";
      } else {
        return "TarifaDefault";
      }
    } else if (rate.includes("2")) {
      return "Tarifa2.0TD";
    } else if (rate.includes("3")) {
      return "Tarifa3.0TD";
    } else {
      return "TarifaDefault";
    }
  };

  const maxDate = dayjs().add(30, "day");
  const isDatePickerDisabled = checkBoxRadioValue !== "selectDate";

  const customDateError: CustomDateError[] = [
    {
      errorType: "maxDate",
      text: t("Datepicker.Debe indicar una fecha válida 30 días"),
    },
  ];

  useEffect(() => {
    if (appliedDay) {
      setAppliedDayFormatted(formatDate(appliedDay?.toDate()));
    }
  }, [appliedDay]);

  useEffect(() => {
    const checkMinPowerError = (): boolean => {
      if (rate.startsWith("3")) {
        return !!!Object.values(newPowers).find(
          (item) => Number(item) >= 15.001,
        );
      }
      return false;
    };

    if (Object.values(newPowers).length === 6) {
      setHasMinPowerError(checkMinPowerError());
    }
  }, [newPowers]);

  return (
    <form>
      <Stack component="div">
        <Stack>
          <Typography
            component="h3"
            variant="h3"
            paddingBottom={{ xs: "1.25rem", md: "1rem" }}
          >
            {t("Gestiones.Elige nuevas potencias")}
          </Typography>
        </Stack>
        {!isLoading && (
          <>
            <Stack>
              <Typography
                component="p"
                variant="body1"
                paddingBottom={{ xs: "2rem", md: "1rem" }}
              >
                {t(`Gestiones.Descripción tarifa.${getPowerMessageKey(rate)}`)}
              </Typography>
            </Stack>

            <Stack>
              <Typography
                component="p"
                variant="body1"
                paddingBottom={{ xs: "2rem", md: "3.5rem" }}
              >
                {t(
                  `Gestiones.Es importante que introduzca las nuevas potencias en orden ascendente${rate.startsWith("2") ? "_Tarifa2" : ""}`,
                )}
              </Typography>
            </Stack>
          </>
        )}
        <Stack
          order={{ xs: "3", md: "4" }}
          paddingBottom={{ xs: "2rem", md: "3.5rem" }}
        >
          <ManagementsUpdateNewPowersTable
            powers={powers}
            newPowers={newPowers}
            setNewPowers={setNewPowers}
            isLoading={isLoading}
            rate={rate}
          />
          {hasMinPowerError && (
            <FormHelperText error>
              <span className="t-icon-CGU" />{" "}
              {t(
                "Gestiones.Debe incluir al menos una tarifa con una potencia superior a 15,001 kW",
              )}
            </FormHelperText>
          )}
          {isLoading && (
            <Box width={"100%"} padding={"1rem 0"}>
              <LoadingComponentComponent
                isLoading={isLoading}
                position="relative"
              />
            </Box>
          )}
        </Stack>
      </Stack>
      <Box component="div" paddingBottom={{ xs: "2rem", md: "3.5rem" }}>
        <Typography component="h3" variant="h3" paddingBottom="1.5rem">
          {t("Gestiones.Día de aplicación")}
        </Typography>
        <RadioGroup
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "1.5rem",
            paddingBottom: "1.5rem",
          }}
          className="powers-radio"
        >
          {appliedDayData.map(({ id, label, value }) => (
            <FormControlLabel
              key={id}
              value={value}
              label={t(`Gestiones.${label}`)}
              onChange={() => {
                setCheckBoxRadioValue(value);
                const appliedDayFormatted =
                  value === "asap"
                    ? "Cuanto antes"
                    : appliedDay
                      ? formatDate(appliedDay?.toDate())
                      : "";
                setAppliedDayFormatted(appliedDayFormatted);
              }}
              control={<Radio disableRipple />}
            />
          ))}
        </RadioGroup>
        <FormControl sx={{ gap: "0.5rem", alignItems: "start" }}>
          <FormLabel htmlFor="dates">
            <Typography fontWeight={600} variant="button">
              {t(`Gestiones.Fecha de aplicación`)}
              <span style={{ color: "red" }}> *</span>
            </Typography>
          </FormLabel>
          <SingleDatePicker
            maxDate={maxDate}
            appliedDay={appliedDay}
            setAppliedDay={setAppliedDay}
            disabled={isDatePickerDisabled}
            disablePast
            error={error}
            setError={setError}
            customDateError={customDateError}
          />

          <Typography variant="caption" fontStyle={"italic"} marginTop={1}>
            {t(`Gestiones.Fecha de aplicación supeditada`)}
          </Typography>
        </FormControl>
      </Box>
      <Box component="div" paddingBottom={{ xs: "2rem", md: "3.5rem" }}>
        <Typography component="h3" variant="h3" paddingBottom="1.5rem">
          {t("Gestiones.Maxímetro")}
        </Typography>
        <RadioGroup
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "1.5rem",
          }}
          className="powers-radio"
        >
          <FormControlLabel
            value={"yes"}
            label={t(`Gestiones.Sí`)}
            onChange={() => setMaximeter(true)}
            control={<Radio disableRipple />}
            checked={maximeter}
          />
          <FormControlLabel
            value={"no"}
            label={t(`Gestiones.No`)}
            onChange={() => setMaximeter(false)}
            control={<Radio disableRipple />}
          />
        </RadioGroup>
      </Box>
      {isAgent && (
        <Box component="div" paddingBottom={{ xs: "2rem", md: "3.5rem" }}>
          <Typography component="h3" variant="h3" paddingBottom="1.5rem">
            {t("Gestiones.Anexo")}
            <span style={{ color: "red" }}> *</span>
          </Typography>
          <Typography component="p" variant="body1">
            {t("Gestiones.Debe subir cualquier tipo de boletín")}
          </Typography>
          <ManagementUploadFile
            downloadDocumentType={"05"}
            downloadButtonText={t(
              "Gestiones.Descarga anexo modificación de potencia contratada",
            )}
            isRequired={true}
            onFileUpload={(id, file) => {
              setRequiredFile(file);
            }}
            onFileRemove={() => {
              setRequiredFile(null);
            }}
          />
        </Box>
      )}
      <Box component="div" paddingBottom={{ xs: "2rem", md: "3.5rem" }}>
        <Typography component="h3" variant="h3" paddingBottom="1.5rem">
          {t("Gestiones.Anexo")}
        </Typography>
        <Typography component="p" variant="body1">
          {t("Gestiones.Opcionalmente puede subir cualquier tipo de boletín")}
        </Typography>
        <ManagementUploadFile
          onFileUpload={(id, file) => {
            setFile(file);
          }}
        />
      </Box>
      <Box component="div" paddingBottom={{ xs: "2rem", md: "3.5rem" }}>
        <Typography component="h3" variant="h3" paddingBottom="1.5rem">
          {t("Gestiones.Datos de contacto")}
        </Typography>
        <Typography component="p" variant="body1" paddingBottom="1.5rem">
          {t("Gestiones.Descripción datos de contacto")}
        </Typography>
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap="12px"
          // maxWidth={{ md: "80%" }}
          width={"100%"}
        >
          <FormControl
            sx={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <FormLabel>
              <Typography
                sx={{
                  width: "190px",
                }}
              >
                {t("Gestiones.Número de teléfono")}
              </Typography>
            </FormLabel>
            <OutlinedInput
              onChange={({
                target: { value },
              }: React.ChangeEvent<HTMLInputElement>) => {
                setContactData({ ...contactData, phone: value });
              }}
              name={"number"}
              type={"number"}
              sx={{
                width: { xs: "100%", md: "330px" },
              }}
            />
          </FormControl>
          <FormControl
            required
            sx={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <FormLabel>
              <Typography
                sx={{
                  width: "190px",
                }}
              >
                {t("Gestiones.Persona de contacto")}
              </Typography>
            </FormLabel>
            <OutlinedInput
              inputProps={{ maxLength: 60 }}
              onChange={({
                target: { value },
              }: React.ChangeEvent<HTMLInputElement>) => {
                setContactData({ ...contactData, contactName: value });
              }}
              name={"contactName"}
              sx={{
                width: { xs: "100%", md: "330px" },
              }}
            />
          </FormControl>
        </Box>
      </Box>
      <Box component="div" paddingBottom={{ xs: "2rem", md: "3.5rem" }}>
        <Typography component="h3" variant="h3" paddingBottom="1.5rem">
          {t("Gestiones.Nota")}
        </Typography>
        <Typography component="p" variant="body1" paddingBottom="1.5rem">
          {t("Gestiones.Opcionalmente puede dejarnos un mensaje")}
        </Typography>
        <FormControl
          sx={{
            display: "flex",
            gap: "1rem",
          }}
        >
          <FormLabel required sx={{ display: "flex", direction: "column" }}>
            <Typography component={"p"}>
              <Typography component={"span"} fontWeight={600}>
                {t("Gestiones.Mensaje")}
              </Typography>
              {` - ${t("Opcional")}`}
            </Typography>
          </FormLabel>
          <TextField
            multiline
            rows={10}
            onChange={({ target: { value } }) => setNotes(value)}
          />
        </FormControl>
      </Box>
      <Box
        component="div"
        display={"flex"}
        alignItems={"flex-start"}
        gap={"0.5rem"}
        paddingBottom={{ xs: "2rem", md: "3.5rem" }}
      >
        <Checkbox
          sx={{ fontSize: "24px" }}
          onChange={(event) => {
            setIsConsentAccepted(event.target.checked);
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
          icon={<span className="t-icon-checkbox-default"></span>}
          checkedIcon={<span className="t-icon t-icon-checkbox-checked"></span>}
        />
        <Typography component="p" variant="body1">
          {t("Gestiones.Doy mi consentimiento")}
        </Typography>
      </Box>
      {!!ocrError && (
        <FormHelperText
          error
          sx={{
            marginLeft: 0,
            marginTop: "8px",
            textAlign: "left",
          }}
        >
          {ocrError === "response_ko"
            ? t(
                "Gestiones.Los datos del documento no coinciden con los registrados",
              )
            : t(
                "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
              )}
        </FormHelperText>
      )}
      {!!ocrError &&
        ocrError === "response_ko" &&
        ocrErrorMessage?.length > 0 && (
          <FormHelperText
            error
            sx={{
              marginLeft: 0,
              marginTop: "8px",
              textAlign: "left",
            }}
          >
            <p>{t("Gestiones.Revise los siguientes campos")}</p>
            <ul>
              {ocrErrorMessage?.map((msg) => (
                <li>{msg}</li>
              ))}
            </ul>
          </FormHelperText>
        )}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          gap: "1rem",
          marginY: "1.5rem",
          flexWrap: "wrap",
        }}
      >
        <ButtonElement
          isLoading={isOcrLoading}
          disabled={isDisabled}
          onClick={(event) => {
            event.preventDefault();
            handleSubmit(
              formatUpdatePowersRequestBody(
                checkBoxRadioValue,
                appliedDay ? appliedDay.toDate() : undefined,
                newPowers,
                notes,
                contactData,
                maximeter,
              ),
            );
          }}
        >
          {t("Gestiones.Solicitar modificación de potencias")}
        </ButtonElement>
        <ButtonElement type="button" onClick={openModal} secondary>
          {t("Cancelar")}
        </ButtonElement>
      </Box>
      <ManagementsCancelProcess open={isModalOpen} handleClose={closeModal} />
    </form>
  );
};

export default ManagementsUpdateNewPowers;
